import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Star, Users } from "lucide-react";
import { Switch } from "../ui/switch";
import { api, errMessage } from "../../lib/api";

const GOALS = [30, 60, 90, 120, 180];

// Sibling Rally: one shared weekly family goal — every child's AI-voice
// practice minutes add up to a single team target. Hitting it earns every
// sibling the same star bonus, so kids cheer each other on.
export const FamilyRally = () => {
  const [data, setData] = useState(null);
  const [history, setHistory] = useState(null);
  const fetched = useRef(false); // single-flight: StrictMode double-invokes effects

  const load = useCallback(() => {
    api.get("/rally").then(({ data: d }) => setData(d)).catch(() => setData(null));
    api.get("/rally/history").then(({ data: h }) => setHistory(h)).catch(() => setHistory(null));
  }, []);
  useEffect(() => {
    if (fetched.current) return;
    fetched.current = true;
    load();
  }, [load]);

  const save = async (enabled, goalMinutes) => {
    try {
      await api.put("/rally/settings", { enabled, goal_minutes: goalMinutes });
      toast.success(enabled
        ? `Sibling Rally on — ${goalMinutes} team minutes a week`
        : "Sibling Rally turned off");
      load();
    } catch (e) {
      toast.error(errMessage(e));
    }
  };

  if (!data) return null;
  const pct = Math.min(100, Math.round((data.total_minutes / data.goal_minutes) * 100));
  return (
    <div className="bg-card rounded-2xl border border-border p-5 space-y-3" data-testid="family-rally-card">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <Users className="h-5 w-5 text-secondary" aria-hidden="true" />
          <div>
            <p className="text-sm font-semibold">Sibling Rally</p>
            <p className="text-xs text-muted-foreground">A shared weekly goal — everyone&apos;s practice minutes add up to one team reward.</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <select value={String(data.goal_minutes)} disabled={!data.enabled}
            onChange={(e) => save(data.enabled, Number(e.target.value))}
            className="h-8 rounded-lg border border-border bg-card px-2 text-xs disabled:opacity-50"
            aria-label="Weekly team goal" data-testid="rally-goal-select">
            {GOALS.map((g) => <option key={g} value={g}>{`${g} min goal`}</option>)}
          </select>
          <Switch checked={Boolean(data.enabled)}
            onCheckedChange={(v) => save(Boolean(v), data.goal_minutes)}
            aria-label="Sibling Rally" data-testid="rally-toggle" />
        </div>
      </div>
      {data.enabled && (
        <>
          <div className="h-3 rounded-full bg-muted overflow-hidden" role="progressbar"
            aria-valuenow={data.total_minutes} aria-valuemin={0} aria-valuemax={data.goal_minutes}>
            <div className="h-full rounded-full bg-amber-400 transition-[width] duration-500"
              style={{ width: `${pct}%` }} data-testid="rally-progress-bar" />
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs font-bold" data-testid="rally-progress-label">
              {data.total_minutes} / {data.goal_minutes} team minutes
            </span>
            {data.per_child.map((c) => (
              <span key={c.child_id}
                className="rounded-full border border-border bg-muted px-2.5 py-0.5 text-[11px] font-semibold"
                data-testid={`rally-child-${c.child_id}`}>
                {c.name} {c.minutes} min
              </span>
            ))}
          </div>
          {data.reached ? (
            <p className="flex items-center gap-1.5 text-xs font-bold text-amber-600" data-testid="rally-reached-banner">
              <Star className="h-4 w-4 fill-amber-400 text-amber-400" aria-hidden="true" />
              Team goal reached — every child earned +{data.reward?.stars_each ?? 10} Star Points!
            </p>
          ) : (
            <p className="text-[11px] text-muted-foreground" data-testid="rally-hint">
              When the family crosses the line, every child earns +10 Star Points for the week.
              Weekend minutes count double!
            </p>
          )}
          {history?.weeks?.length > 0 && (
            <div className="pt-1 space-y-1">
              <p className="text-[10px] font-bold uppercase tracking-wide text-muted-foreground">Last 8 weeks</p>
              <div className="flex items-end gap-1.5" data-testid="rally-history-strip">
                {history.weeks.map((w) => (
                  <div key={w.week} className="flex flex-col items-center gap-0.5"
                    role="img"
                    aria-label={`Week ${w.week}: ${w.minutes} team minutes${w.won ? ", goal reached" : ""}${w.current ? " (this week)" : ""}`}
                    title={`${w.week}: ${w.minutes} min${w.won ? " — goal reached" : ""}`}
                    data-testid={`rally-history-${w.week}`}>
                    {w.won
                      ? <Star className="h-3 w-3 fill-amber-400 text-amber-400" aria-hidden="true" />
                      : <span className="h-3 w-3" aria-hidden="true" />}
                    <div className={`w-4 h-10 rounded-sm overflow-hidden flex items-end bg-muted ${w.current ? "ring-1 ring-ring" : ""}`}>
                      <div className={`w-full ${w.won ? "bg-amber-400" : "bg-emerald-300"}`}
                        style={{ height: `${Math.min(100, Math.round((w.minutes / history.goal_minutes) * 100))}%` }} />
                    </div>
                  </div>
                ))}
                <span className="text-[11px] font-bold text-muted-foreground pl-2" data-testid="rally-wins-count">
                  {history.wins} win{history.wins === 1 ? "" : "s"} in 8 weeks
                </span>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};
